import { CategoryNameToId } from "./enums";

const GLB_EXTENSIONS = ["glb", "gltf"];
const SCRIPT_ASSET_EXTENSION = "js";
const DEFAULT_ASSET_TYPE = "png";
const WIDTH_ATTRIBUTE_NAMES = ["Width", "Widh"];
const HEIGHT_ATTRIBUTE_NAME = "Height";
const DEPENDENCY_KEY_SEPARATOR = "::";

function inferAssetType(url) {
  const extension = url?.split(".").pop()?.toLowerCase();
  if (GLB_EXTENSIONS.includes(extension)) {
    return "glb";
  }
  if (extension === SCRIPT_ASSET_EXTENSION) {
    return SCRIPT_ASSET_EXTENSION;
  }
  return DEFAULT_ASSET_TYPE;
}

function normalizeAttributeValues(attributeValues) {
  const entries = Object.entries(attributeValues || {});
  const isWidth = ([name]) => WIDTH_ATTRIBUTE_NAMES.includes(name);
  const isHeight = ([name]) => name === HEIGHT_ATTRIBUTE_NAME;
  const ordered = [
    ...entries.filter(isWidth),
    ...entries.filter(isHeight),
    ...entries.filter((entry) => !isWidth(entry) && !isHeight(entry)),
  ];
  return ordered.map(([name, value]) => ({ name, value }));
}

function normalizeAsset(asset) {
  return {
    asset_url: asset.assetUrl,
    preview_image_url: asset.previewImageUrl,
    asset_type: inferAssetType(asset.assetUrl),
    attribute_values: normalizeAttributeValues(asset.attributeValues),
  };
}

function buildDependencyKey(groupKey, componentName, variationKey) {
  return [groupKey, componentName, variationKey].join(DEPENDENCY_KEY_SEPARATOR);
}

function buildDependencyIndex(product) {
  const index = new Map();
  (product.componentGroups || []).forEach((group) => {
    (group.components || []).forEach((component) => {
      (component.variations || []).forEach((variation) => {
        index.set(
          buildDependencyKey(group.key, component.name, variation.key),
          variation.id
        );
      });
    });
  });
  return index;
}

function resolveDependencies(dependencies, dependencyIndex) {
  return (dependencies || [])
    .map((dependency) =>
      dependencyIndex.get(
        buildDependencyKey(
          dependency.group,
          dependency.component,
          dependency.variation
        )
      )
    )
    .filter((id) => id !== undefined)
    .map((id) => ({ depends_on_variation_id: id }));
}

function normalizeVariation(variation, componentId, dependencyIndex) {
  return {
    id: variation.id,
    component_id: componentId,
    key: variation.name,
    original_key: variation.key,
    is_default: Boolean(variation.isDefault),
    assets: (variation.assets || []).map(normalizeAsset),
    dependencies: resolveDependencies(variation.dependencies, dependencyIndex),
  };
}

function normalizeComponent(component, dependencyIndex) {
  return {
    id: component.id,
    name: component.name,
    required_choice: Boolean(component.requiredChoice),
    layer_order: component.order,
    available_variations: (component.variations || []).map((variation) =>
      normalizeVariation(variation, component.id, dependencyIndex)
    ),
  };
}

function hasSelectableContent(component) {
  return component.name !== "" || (component.variations || []).length > 0;
}

function normalizeComponentGroup(group, dependencyIndex) {
  return {
    id: group.id,
    name: group.name,
    components: (group.components || [])
      .filter(hasSelectableContent)
      .map((component) => normalizeComponent(component, dependencyIndex)),
  };
}

export function normalizeV2Product(product) {
  const dependencyIndex = buildDependencyIndex(product);
  return {
    id: product.id,
    name: product.name,
    category_id: CategoryNameToId[product.category] ?? null,
    product_info: { description: product.description },
    component_groups: (product.componentGroups || []).map((group) =>
      normalizeComponentGroup(group, dependencyIndex)
    ),
  };
}
